import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../lib/api-client";

export interface LatestLeadRow {
  id: string;
  name: string | null;
  company: string | null;
  email: string | null;
  phoneE164: string | null;
  status: string;
  source: string;
  sourceCampaignName: string | null;
  sourceAdName: string | null;
  sourceFormId: string | null;
  assignedTo: string | null;
  lastActivityAt: string | null;
  lastLoggedActivityType: string | null;
  lastLoggedActivityAt: string | null;
  createdAt: string;
}

export interface OverviewResponse {
  days: number;
  stats: {
    total: number;
    converted: number;
    convertedRate: number;
    unattended24h: number;
    newToday: number;
  };
  funnel: { status: string; count: number }[];
  sources: { source: string; campaign: string | null; count: number }[];
  leaderboard: {
    userId: string;
    name: string;
    email: string;
    leadCount: number;
    activityCount: number;
    convertedCount: number;
  }[];
  hot: LatestLeadRow[];
  latestDirect: LatestLeadRow[];
  latestMeta: LatestLeadRow[];
}

export function useOverviewQuery(workspaceSlug: string | null, days = 30) {
  return useQuery({
    enabled: !!workspaceSlug,
    queryKey: ["analytics", "overview", workspaceSlug, days],
    queryFn: () =>
      apiClient.get<OverviewResponse>("/api/analytics/overview", {
        params: { workspace: workspaceSlug ?? undefined, days },
      }),
    staleTime: 60_000,
  });
}

export interface FollowUpDueRow {
  id: string;
  name: string | null;
  company: string | null;
  phoneE164: string | null;
  status: string;
  assignedTo: string | null;
  nextFollowUpAt: string;
  lastActivityAt: string | null;
}

// Leads whose follow-up is due today or earlier. Server scopes by role: sales
// see their own, managers/admins see the whole workspace.
export function useFollowUpsDueQuery(workspaceSlug: string | null) {
  return useQuery({
    enabled: !!workspaceSlug,
    queryKey: ["analytics", "follow-ups-due", workspaceSlug],
    queryFn: () =>
      apiClient.get<{ leads: FollowUpDueRow[] }>("/api/analytics/follow-ups-due", {
        params: { workspace: workspaceSlug ?? undefined },
      }),
    staleTime: 60_000,
  });
}
